import router from '@/router';
import { defineStore } from 'pinia';
import { fetchCompanies, fetchSuspects, fetchProspects, downloadProspects } from "@/services/ppApi";
import { useAccountStore } from '@/stores/account';
import { useUserStore } from '@/stores/user';
import { useProspectStore } from '@/stores/prospect';
import { useAudienceStore } from '@/stores/audience';
import { useApiCreditsStore } from '@/stores/apiCredits';
import { defaultCompanyQueryOptions, generateCompanyQueryParams, generateProspectQueryParams } from '@/utils/builder';
import { prefetchManager } from '@/services/ppPrefetchManager';

export const useCompaniesStore = defineStore('companies', {
    state: () => {
        const accountStore = useAccountStore();
        const accountId = accountStore?.account?.account_id ? accountStore.account.account_id : null;
        const userCompaniesSettings = useUserStore().getCompaniesSettings(accountId);
        const userProspectsSettings = useUserStore().getProspectsSettings(accountId);

        const defaultOptions = defaultCompanyQueryOptions;

        const defaultProspectsOptions = {
            lists: [],
            sort: 'default',
            label: 'all',
            tags: [],
            NOT_tags: [],
            created: 'all',
            updated: 'all',
            owner: 'all',
            contacts: 'all',
            visits: 'all',
            last_visit: 'all'
        };

        const defaultPagination = {
            limit: '10',
            page: 1
        };
        return {
            mode: 'all',
            audienceId: null,
            quickSearch: '',
            accountId: accountId,
            companies: [],
            downloadInProgress: false,
            downloadLinks: {},
            loading: false,
            error: null,
            found: 0,
            total: 0,
            qid: null,
            audienceQid: null,
            defaultOptions: defaultOptions,
            options: userCompaniesSettings?.options 
                ? JSON.parse(JSON.stringify({ ...defaultOptions, ...userCompaniesSettings.options }))
                : JSON.parse(JSON.stringify(defaultOptions)),
            defaultProspectsOptions: defaultProspectsOptions,
            prospectsOptions: userProspectsSettings?.options ? { ...defaultProspectsOptions, ...userProspectsSettings.options } : { ...defaultProspectsOptions },
            audienceOptions: null,
            defaultPagination: defaultPagination,
            pagination: { ...defaultPagination },
            paginationBackup: null,
            currentCompanyIndex: null,
            abortController: null,
            showPanel: false,
            nextSuspectParams: null,
            noCredits: false,
            statsTimer: null,
        }
    },
    getters: {
        hasDefaultOptions(state) {
            if(this.audienceId) {
                return JSON.stringify(state.options) === JSON.stringify({...state.defaultOptions, ...state.audienceOptions});
            } else {
                return JSON.stringify(state.options) === JSON.stringify(state.defaultOptions);
            }
        },
        hasDefaultProspectsOptions(state) {
            return JSON.stringify(state.prospectsOptions) === JSON.stringify(state.defaultProspectsOptions);
        },
        isFirstCompany(state) {
            return state.currentCompanyIndex === 0 && state.pagination.page === 1;
        },
        isLastCompany(state) {
            const isLastItemOnPage = state.currentCompanyIndex === state.companies.length - 1;
            const isLastPage = state.pagination.page * state.pagination.limit >= state.total;
            return isLastItemOnPage && isLastPage;
        },
        hasCompletedSuspects(state) {
            return state.error?.error_code === 'no_data';
        }
    },
    actions: {
        resetCompanies() {
            if (this.statsTimer) {
                clearTimeout(this.statsTimer);
            }
            this.$reset();
        },
        async getCompanies(forceFetch = false) {
            if(forceFetch || (this.companies && this.companies.length < 1)) {
                await this.loadCompanies();
            }
            return this.companies.length > 0;
        },
        async loadCompanies(id = null, updateCompany = false) {
            if (this.abortController) {
                this.abortController.abort();
                this.abortController = null;
            }

            if (this.statsTimer) {
                clearTimeout(this.statsTimer);
            }

            prefetchManager.clearQueue();

            this.abortController = new AbortController();
            let aborted = false;
            let companyUpdated = false;

            this.loading = true;
            this.error = null;

            try {
                const accountStore = useAccountStore();
                const queryParams = generateCompanyQueryParams(this.options, false);
                const prospectQueryParams = generateProspectQueryParams(this.prospectsOptions);

                const paginationOptions = {
                    [this.mode !== 'prospects' ? 'rows' : 'limit']: this.pagination.limit,
                    page: this.pagination.page
                }
                
                const defaultOptions = {
                    account_id: accountStore.account.account_id,
                    add_details: '*',
                    add_fields: 'label,owner,tags',
                    suspectsformat: 1,
                    add_contacts: 3,
                    and_or: 'AND'
                }

                const defaultSuspectOptions = { 
                    account_id: accountStore.account.account_id,
                    add_details: '*',
                    prefetch: 1,
                    rows: 1,
                    and_or: 'AND'
                };

                const defaultProspectsOptions = {
                    account_id: accountStore.account.account_id,
                    add_details: '*',
                    add_fields: 'label,owner,tags',
                    add_contacts: 3,
                    ...(this.audienceId ? { lists: this.qid || this.audienceQid } : {})
                }

                const payload = id !== null ? {
                    id: id,
                    ...paginationOptions,
                    ...(this.mode === 'prospects' ? defaultProspectsOptions : defaultOptions),
                } : this.quickSearch ? {
                    ...(this.mode === 'all' && this.audienceId ? queryParams : {}),
                    [this.mode !== 'prospects' ? (this.audienceId ? 'names' : 'suggest') : 'text']: this.quickSearch,
                    ...paginationOptions,
                    ...(this.mode === 'prospects' ? defaultProspectsOptions : defaultOptions)
                } : this.mode === 'new' && this.nextSuspectParams ? {
                    ...this.nextSuspectParams
                } : this.mode === 'new' ? {
                    ...queryParams,
                    ...defaultSuspectOptions,
                } : this.mode === 'prospects' ? {
                    ...(!this.hasDefaultProspectsOptions && prospectQueryParams),
                    ...paginationOptions,
                    ...defaultProspectsOptions,
                } : {
                    ...queryParams,
                    ...paginationOptions,
                    ...defaultOptions,
                }

                const response =
                    this.mode === 'prospects' ? await fetchProspects(payload, this.abortController.signal) :
                    this.mode === 'new' && !id ? await fetchSuspects(payload, this.abortController.signal) :
                    await fetchCompanies(payload, this.abortController.signal);

                if(!response) {
                    aborted = true;
                    return;
                }

                useApiCreditsStore().updatePpCredits({
                    ...(response.data?.monthly_credits ? { monthly_credits: response.data.monthly_credits } : {}),
                    ...(response.data?.credits_used_month ? { credits_used_month: response.data.credits_used_month } : {}),
                    ...(response.data?.monthly_perc ? { monthly_perc: response.data.monthly_perc } : {})
                });

                if(response.data?.no_credits) {
                    this.noCredits = true;
                } else {
                    this.noCredits = false;
                }

                if(response.data?.companies?.length > 1) {
                    this.companies = response.data.companies;
                } else if (this.mode === 'new' && response.data?.companies?.length === 1) {
                    const index = this.companies.findIndex(company => company.id === response.data.companies[0].id);
                    if(index !== -1 && updateCompany) {
                        this.companies[index] = response.data.companies[0];
                        companyUpdated = true;
                    } else if (this.nextSuspectParams) {
                        const index = this.companies.findIndex(company => company.id === response.data.companies[0].id);
                        if(index !== -1) {
                            this.pagination.page -= 1;
                            this.companies.splice(index, 1);
                        }
                        this.companies.push(response.data.companies[0]);
                    } else {
                        this.companies = [response.data.companies[0]];
                    }
                } else if (response.data?.companies?.length === 1) {
                    const index = this.companies.findIndex(company => company.id === response.data.companies[0].id);
                    if(index !== -1 && updateCompany) {
                        this.companies[index] = response.data.companies[0];
                        companyUpdated = true;
                    } else {
                        this.companies = [response.data.companies[0]];
                    }
                } else {
                    this.companies = [];
                }

                if(!companyUpdated) {
                    this.found = response.data?.found > this.pagination.limit ? this.pagination.limit : response.data?.found || 0;
                    this.total = response.data?.total ? response.data.total : this.pagination.page > 1 && this.total > 0 ? this.total : response.data?.found || 0;

                    if(this.total > 1 && !this.hasDefaultOptions && response.data?.qid) {
                        this.qid = response.data.qid;
                    } else {
                        this.qid = null;
                    }

                    if(this.mode === 'all' && !this.quickSearch && this.qid && (!this.audienceQid || this.audienceQid !== this.qid)) {
                        this.statsTimer = setTimeout(() => {
                            this.preloadStats();
                        }, 5000);
                    }

                    if(this.mode === 'new' && response.data?.companies?.length > 0 && response.data?.next) {
                        const url = new URL(response.data.next);
                        this.nextSuspectParams = Object.fromEntries(url.searchParams.entries());

                        this.openCompany(response.data.companies[0]);
                        const prefetchLinks = response.data.prefetch_links || null;
                        if (Array.isArray(prefetchLinks) && prefetchLinks.length > 0) {
                            prefetchManager.enqueue(prefetchLinks, 1000);
                        }
                        
                    } else {
                        this.nextSuspectParams = null;
                    }
                }
            } catch (error) {
                this.companies = [];
                this.found = 0;
                this.total = 0;
                this.qid = null;
                this.error = error?.response?.data || error;
            } finally {
                if (!aborted) {
                    this.loading = false;
                }
                this.abortController = null;
            }
        },
        preloadStats() {
            if (!this.qid) return;
            const accountStore = useAccountStore();
            const account_id = accountStore.account.account_id;
            if(this.mode === 'all' && !this.quickSearch && this.qid && (!this.audienceQid || this.audienceQid !== this.qid) && this.total > 1 && this.total < 25000 && account_id) {
                const queryParams = generateCompanyQueryParams(this.options, false);   
                const payload = {
                    ...queryParams,
                    account_id,
                    and_or: 'AND',
                    add_stats: 1,
                    rows: 0
                }
                try {
                    fetchCompanies(payload);
                } catch {
                    // Nothing to see here.
                }
            }
        },
        updateAudience(audience = null) {
            if (audience?.audience_id && audience.audience_id === this.audienceId && audience.company_options && JSON.parse(audience.company_options) === this.audienceOptions) return;
            
            this.audienceId = audience?.audience_id ? audience.audience_id : null;
            this.audienceOptions = audience?.company_options ? JSON.parse(audience.company_options) : null;
            this.qid = audience?.qid ? audience.qid : null;
            this.audienceQid = audience?.qid ? audience.qid : null;

            if(this.audienceId && this.audienceOptions) {
                this.options = JSON.parse(JSON.stringify({ ...this.defaultOptions, ...this.audienceOptions }));
                this.prospectsOptions = JSON.parse(JSON.stringify(this.defaultProspectsOptions));
            } else {
                const userCompaniesSettings = useUserStore().getCompaniesSettings(this.accountId);
                this.options = JSON.parse(JSON.stringify(
                    userCompaniesSettings?.options 
                    ? { ...this.defaultOptions, ...userCompaniesSettings.options } 
                    : { ...this.defaultOptions }
                ));
                const userProspectsSettings = useUserStore().getProspectsSettings(this.accountId);
                this.prospectsOptions = userProspectsSettings?.options 
                    ? JSON.parse(JSON.stringify({ ...this.defaultProspectsOptions, ...userProspectsSettings.options }))
                    : JSON.parse(JSON.stringify(this.defaultProspectsOptions));
            }

            this.pagination.page = 1;
            this.downloadLinks = {};
            this.quickSearch = '';
        },
        updateMode(mode = null) {
            if (!mode || mode === this.mode) return;

            this.mode = mode;
            this.pagination.page = 1;
            this.downloadLinks = {};
            this.quickSearch = '';
        },
        updateOptions(newOptions, type = 'companies') {
            if (type === 'prospects') {
                this.prospectsOptions = JSON.parse(JSON.stringify({ ...this.prospectsOptions, ...newOptions }));
            } else if (type === 'companies') {
                this.options = JSON.parse(JSON.stringify({ ...this.options, ...newOptions }));
            } else {
                return;
            }

            if(!this.audienceId) {
                this.saveCompaniesSettings();
            }
            this.saveProspectsSettings();
            
            this.pagination.page = 1;
            this.downloadLinks = {};
            this.qid = null;
            this.loadCompanies();
        },
        resetOptions(type = 'companies') {
            if (type === 'prospects') {
                this.prospectsOptions = JSON.parse(JSON.stringify(this.defaultProspectsOptions));
            } else if (type === 'companies') {
                if(this.audienceId && this.audienceOptions) {
                    this.options = JSON.parse(JSON.stringify({ ...this.defaultOptions, ...this.audienceOptions }));
                } else {
                    this.options = JSON.parse(JSON.stringify(this.defaultOptions));
                }
            }

            if(!this.audienceId) {
                this.saveCompaniesSettings();
            }
            this.saveProspectsSettings();

            this.qid = null;
            this.pagination.page = 1;
            this.downloadLinks = {};
            this.loadCompanies();
        },
        saveCompaniesSettings() {
            const userStore = useUserStore();
            const accountStore = useAccountStore();
            userStore.saveCompaniesSettings(accountStore.account.account_id, { options: this.options });
        },
        saveProspectsSettings() {
            const userStore = useUserStore();
            const accountStore = useAccountStore();
            userStore.saveProspectsSettings(accountStore.account.account_id, { options: this.prospectsOptions });
        },
        routeToMode(mode = null, exitAudience = false) {
            if (!mode) return;

            const currentRoute = router.currentRoute.value;
            const routeMap = {
                'all': { name: 'companies' },
                'new': { name: 'suspects' },
                'prospects': { name: 'prospects' }
            };

            const routeMapAudience = {
                'all': { name: 'audienceCompanies' },
                'new': { name: 'audienceSuspects' },
                'prospects': { name: 'audienceProspects' }
            };

            const audienceStore = useAudienceStore();
            const audienceId = audienceStore.audience?.audience_id || null;

            const map = audienceId && !exitAudience ? routeMapAudience : routeMap;

            if (map[mode] && currentRoute.name !== map[mode].name) {
                const payload = audienceId && !exitAudience ? {
                    name: map[mode].name,
                    params: { id: audienceId }
                } : map[mode];
                router.push(payload);
            }
        },
        updateQuickSearch(val = null) {
            if(!this.quickSearch && val && JSON.stringify(this.pagination) !== JSON.stringify(this.defaultPagination)) {
                this.paginationBackup = { ...this.defaultPagination, ...this.pagination };
            }
            
            if(!val && this.paginationBackup) {
                this.pagination = { ...this.defaultPagination, ...this.paginationBackup };
                this.paginationBackup = null;
            } else {
                this.pagination = { ...this.defaultPagination };
            }

            this.quickSearch = val || '';
            this.loadCompanies();
        },
        togglePanel(newVal = null) {
            if (newVal !== null) {
                this.showPanel = newVal;
            } else {
                this.showPanel = !this.showPanel;
            }
        },
        async downloadCompanies(all = false, audienceQid = null) {
            this.downloadInProgress = true;

            try {
                const accountStore = useAccountStore();
                const params = all ? {} : generateProspectQueryParams(this.prospectsOptions);
                const payload = {
                    ...params,
                    account_id: accountStore.account.account_id,
                    add_contacts: 100,
                    export: 1,
                    sort_by: 'default',
                    ...(audienceQid || this.audienceId ? { lists: audienceQid || this.audienceQid } : {})
                };

                const response = await downloadProspects(payload);
                if(response?.data?.export_url_csv || response?.data?.export_url_xlsx || response?.data?.export_url_zip) {
                    this.downloadLinks = {
                        export_url_csv: response.data.export_url_csv || null,
                        export_url_xlsx: response.data.export_url_xlsx || null,
                        export_url_zip: response.data.export_url_zip || null
                    };
                    return true;
                } else {
                    this.downloadLinks = {};
                    return false;
                }
            } catch {
                this.downloadLinks = {};
                return false;
            } finally {
                this.downloadInProgress = false;
            }
        },
        updatePagination(newPagination) {
            this.pagination = { ...this.pagination, ...newPagination };
            this.loadCompanies();
        },
        async loadNextCompany() {
            if (this.currentCompanyIndex === null) return;
            
            this.currentCompanyIndex += 1;
            
            if (this.currentCompanyIndex >= this.companies.length) {
                this.pagination.page += 1;
                await this.loadCompanies();

                if(this.mode !== 'new') {
                    this.currentCompanyIndex = 0;
                }
            } else if (this.mode === 'new') {
                this.pagination.page += 1;
            }
    
            const nextId = this.companies[this.currentCompanyIndex]?.id;

            if (nextId) {
                this.openCompany({id: nextId});
            } else if (this.mode === 'new' && this.companies?.length > 1) {
                this.currentCompanyIndex = this.companies.length - 1;
                this.pagination.page = this.currentCompanyIndex + 1;
                this.openCompany({id: this.companies[this.currentCompanyIndex].id});
            } else {
                if(this.pagination.page > 1 && this.mode !== 'new') {
                    this.pagination.page -= 1;
                }
                this.closeCompany();
            }
        },
        async loadPreviousCompany() {
            if (this.currentCompanyIndex === null) return;
            
            this.currentCompanyIndex -= 1;

            if(this.mode === 'new') {
                this.pagination.page -= 1;
            }
    
            if (this.currentCompanyIndex < 0) {
                if (this.pagination.page > 1) {
                    this.pagination.page -= 1;
                    await this.loadCompanies();
                    this.currentCompanyIndex = this.companies.length - 1;
                } else {
                    this.currentCompanyIndex = 0;
                }
            }
    
            const prevId = this.companies[this.currentCompanyIndex]?.id;
            if (prevId) {
                this.openCompany({id: prevId});
            }
        },
        openCompany(company) {
            if(!company || !company.id) {
                this.closeCompany();
                return;
            } else {
                const audienceStore = useAudienceStore();
                const audienceId = audienceStore.audience?.audience_id || null;

                if(audienceId) {
                    router.push({ name: 'audienceCompany', params: { id: audienceId, companyId: company.id } });
                } else {
                    router.push({ name: 'company', params: { id: company.id } });
                }
            }
        },
        closeCompany() {
            this.resetCompany();
            this.routeToMode(this.mode);
        },
        resetCompany() {
            this.currentCompanyIndex = null;
            useProspectStore().$reset();
        },
        companyHasDetails(company) {
            if(!company || !company.details) return false;
            return Object.keys(company.details).length > 2;
        },
        async loadCompany(id) {
            if(!id) {
                this.resetCompany();
                return false;
            }

            const index = this.companies.findIndex(company => company.id === id);
            const company = this.companies[index] || null;

            if(!this.companyHasDetails(company)) { // Arbitrary check for details --> needs to be improved
                if(company) {
                    await this.loadCompanies(id, true);
                } else {
                    await this.loadCompanies(id);
                }

                if(this.companies.length > 0) {
                    this.currentCompanyIndex = index !== -1 ? index : 0;

                    if(this.mode === 'new') {
                        this.pagination.page = this.currentCompanyIndex + 1;
                    }
                    useProspectStore().setProspect(this.companies[index === -1 ? 0 : index]);
                    if(this.companyHasDetails(this.companies[index === -1 ? 0 : index])) {
                        this.qualifyCompany(-1);
                    }
                    return true;
                } else {
                    this.resetCompany();
                    return false;
                }
            } else {
                this.currentCompanyIndex = index !== -1 ? index : 0;

                if(this.mode === 'new') {
                    this.pagination.page = this.currentCompanyIndex + 1;
                }
                useProspectStore().setProspect(company);
                if(this.companyHasDetails(company)) {
                    this.qualifyCompany(-1);
                }
                return true;
            }
        },
        async qualifyCompany(label) {
            const prospectStore = useProspectStore();
            let hasLabel = prospectStore.prospect.label === 0 || prospectStore.prospect.label === 1 || prospectStore.prospect.label === -1;

            if(label !== -1 || (label === -1 && !hasLabel)) {
                const result = await prospectStore.updateLabel(label);
                if(result) {
                    this.companies[this.currentCompanyIndex] = prospectStore.prospect;

                    if(!hasLabel) {
                        const creditStore = useApiCreditsStore();
                        const credits = creditStore.ppCredits;
                        const monthly_credits = credits?.monthly_credits || 0;
                        const credits_used_month = credits?.credits_used_month || 0;
                        const newCredits = {
                            monthly_credits: monthly_credits,
                            credits_used_month: credits_used_month + 1,
                            monthly_perc: Math.round((credits_used_month + 1) / monthly_credits * 100)
                        };
                        creditStore.updatePpCredits(newCredits);
                    }
                }
            }
        },
    }
});
